import { supabase } from '../lib/supabase';
import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';
import { paginate, filterBar, pager } from '../shared/lib/tableControls';

export class ListingsPage {
  private container: HTMLElement;
  private listings: any[] = [];
  private filteredListings: any[] = [];
  private currentPage = 1;
  private readonly itemsPerPage = 25;
  private defaultMgmtFee = 20;
  private searchQuery = '';
  private filterCity = '';
  private filterBedrooms = '';
  private filterStatus = '';
  private excluded = new Set<string>(); // listing ids hidden from the rest of the dashboard

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    try {
      const res = await fetch('/api/settings');
      if (res.ok) {
        const s = await res.json();
        this.defaultMgmtFee = parseFloat(s.default_mgmt_fee_percent) || 20;
      }
    } catch (_) {}

    this.container.innerHTML = `
      <div class="page-content">

        <div class="dash-head">
          <h1>All Listings</h1>
          <p>The portfolio roster — every property, its attributes, fee rate, and channel links. Use Exclude to hide a listing from every other page without deleting it.</p>
        </div>

        <!-- Summary KPIs -->
        <div class="kpi-grid cols-3" id="listings-kpis"></div>

        <!-- Filters -->
        <div class="card" style="border-radius:var(--radius-lg) var(--radius-lg) 0 0;">
          <div class="card-body" style="padding:var(--space-3) var(--space-4);">
            <div style="display:flex; align-items:flex-start; gap:var(--space-3); flex-wrap:wrap;">
              <div id="listings-controls" style="flex:1 1 auto; min-width:0;"></div>
              <div style="display:flex; gap:var(--space-2); flex-shrink:0;">
                <button class="btn btn-secondary btn-sm" id="import-new-listings" title="Pull listings from Hostaway that aren't in the system yet — existing listings are never modified">⇩ Import new from Hostaway</button>
                <button class="btn btn-secondary btn-sm" id="refresh-listings">↺ Refresh</button>
                <button class="btn btn-secondary btn-sm" id="export-listings">↓ CSV</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="card" style="border-radius:0 0 var(--radius-lg) var(--radius-lg); border-top:none; margin-top:0;">
          <div class="u-table-wrap">
            <table class="data-table" style="min-width:960px;">
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>City</th>
                  <th class="num" style="text-align:center;">BR</th>
                  <th class="num" style="text-align:center;">Guests</th>
                  <th style="text-align:center;">Rating ${infoIcon('avg_rating')}</th>
                  <th class="num" style="text-align:center;">Mgmt% ${infoIcon('management_fee')}</th>
                  <th>Channels</th>
                  <th style="text-align:center;">Actions</th>
                </tr>
              </thead>
              <tbody id="listings-table-body">
                <tr><td colspan="8" style="padding:var(--space-8);">${LoadingSpinner.render()}</td></tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div id="listings-pager" style="padding:0 var(--space-4) var(--space-2);"></div>
        </div>

        ${PageFooter.render([
          { abbr: 'BR',     full: 'Bedrooms',                  note: 'number of bedrooms' },
          { abbr: 'Rating', full: 'Average Review Rating',     note: 'average guest review score out of 5.0' },
          { abbr: 'Mgmt%',  full: 'Management Fee %',          note: 'commission rate for this property' },
        ])}
      </div>

      <!-- Edit Modal -->
      <div class="modal-overlay" id="edit-listing-modal">
        <div class="modal" style="max-width:560px; width:100%;">
          <h3>Edit Listing</h3>
          <input type="hidden" id="edit-listing-id">
          <div style="margin-bottom:14px;">
            <label>Listing Name</label>
            <div id="edit-listing-name" style="font-weight:500; font-size:13px; color:var(--text-muted); margin-top:4px;"></div>
          </div>
          <div style="display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; margin-bottom:14px;">
            <div>
              <label>Short Name (display)</label>
              <input type="text" id="edit-short-name" class="settings-input" style="width:100%;">
            </div>
            <div>
              <label>Management Fee %</label>
              <input type="number" id="edit-mgmt-fee" class="settings-input" style="width:100%;" min="0" max="100" step="0.1">
            </div>
          </div>
          <div style="border-top:1px solid var(--border); padding-top:14px; margin-bottom:14px;">
            <div style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:10px;">Channel Links</div>
            <div style="display:grid; gap:10px;">
              <div>
                <label style="font-size:12px;">WCH URL <span style="color:var(--text-muted); font-weight:400;">(book.westcoasthomestays.com/listings/…)</span></label>
                <input type="url" id="edit-wch-url" class="settings-input" style="width:100%; font-size:12px;" placeholder="https://book.westcoasthomestays.com/listings/…">
              </div>
              <div>
                <label style="font-size:12px;">Airbnb URL <span style="color:var(--text-muted); font-weight:400;">(airbnb.com/rooms/…)</span></label>
                <input type="url" id="edit-airbnb-url" class="settings-input" style="width:100%; font-size:12px;" placeholder="https://www.airbnb.com/rooms/…">
              </div>
              <div>
                <label style="font-size:12px;">VRBO URL</label>
                <input type="url" id="edit-vrbo-url" class="settings-input" style="width:100%; font-size:12px;" placeholder="https://www.vrbo.com/…">
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" id="cancel-edit-btn">Cancel</button>
            <button class="btn btn-primary" id="save-listing-btn">Save Changes</button>
          </div>
        </div>
      </div>
    `;

    await this.loadData();
    this.bindEvents();
  }

  // ── Data ─────────────────────────────────────────────────────

  private async loadData(): Promise<void> {
    try {
      const [{ data, error }, exRes] = await Promise.all([
        supabase.from('listings').select('*').order('name'),
        fetch('/api/listings/exclusions').then(r => r.ok ? r.json() : { excluded: [] }).catch(() => ({ excluded: [] })),
      ]);
      if (error) throw error;
      this.listings = data || [];
      this.excluded = new Set((exRes.excluded || []).map(String));
      this.currentPage = 1;
      this.renderTable();
      this.updateBadge();
    } catch (err) {
      console.error('Failed to load listings:', err);
      const tb = document.getElementById('listings-table-body');
      if (tb) tb.innerHTML = `<tr><td colspan="8">${emptyState('Could not load listings', 'Something went wrong fetching the portfolio roster. Try Refresh, or reload the page.', '⚠️')}</td></tr>`;
    }
  }

  // ── Table ─────────────────────────────────────────────────────

  private renderTable(): void {
    const tbody = document.getElementById('listings-table-body');
    if (!tbody) return;

    // Recompute the filtered set over the already-loaded listings.
    this.computeFiltered();

    // Headline KPI cards (presentational summary of the loaded roster).
    this.renderKpis();

    // Filter bar (search + city + bedrooms + status + count).
    const controls = document.getElementById('listings-controls');
    if (controls) {
      const cities = [...new Set(
        this.listings.map(l => l.city || l.market_locality).filter(Boolean)
      )].sort();
      const statuses = [...new Set(
        this.listings.map(l => l.status).filter(Boolean)
      )].sort();
      const countTxt = `${this.filteredListings.length} listing${this.filteredListings.length !== 1 ? 's' : ''}`
        + (this.excluded.size ? ` · ${this.excluded.size} excluded` : '');
      controls.innerHTML = filterBar({
        searchId: 'listings-search',
        searchValue: this.searchQuery,
        searchPlaceholder: 'Search name or city…',
        selects: [
          { id: 'listings-city-filter', value: this.filterCity, options: [
            { value: '', label: 'All Cities' },
            ...cities.map(c => ({ value: String(c), label: String(c) })),
          ] },
          { id: 'listings-bedrooms-filter', value: this.filterBedrooms, options: [
            { value: '', label: 'Any BR' },
            { value: '1', label: '1 BR' },
            { value: '2', label: '2 BR' },
            { value: '3', label: '3 BR' },
            { value: '4', label: '4 BR' },
            { value: '5', label: '5+ BR' },
          ] },
          { id: 'listings-status-filter', value: this.filterStatus, options: [
            { value: '', label: 'All statuses' },
            ...statuses.map(s => ({ value: String(s), label: String(s) })),
          ] },
        ],
        countLabel: countTxt,
      });
    }

    const pg = paginate(this.filteredListings, this.currentPage, this.itemsPerPage);
    this.currentPage = pg.page; // adopt the clamped page
    const page = pg.items;

    // Pager.
    const pagerEl = document.getElementById('listings-pager');
    if (pagerEl) {
      pagerEl.innerHTML = pager({
        idPrefix: 'listings', page: pg.page, pages: pg.pages, total: pg.total, pageSize: this.itemsPerPage,
      });
    }

    if (!page.length) {
      tbody.innerHTML = `<tr><td colspan="8">${emptyState('No listings found', 'No properties match the current filters. Clear the search or filters to see the full roster.', '🏠')}</td></tr>`;
      this.bindTableControls();
      return;
    }

    tbody.innerHTML = page.map(l => {
      const name        = l.short_name || l.name || 'Unknown';
      const ratingNum   = l.review_score != null ? parseFloat(l.review_score) : NaN;
      const rating      = Number.isFinite(ratingNum) ? ratingNum : null;
      const mgmtNum     = Number(l.mgmt_fee_percent ?? this.defaultMgmtFee);
      const mgmt        = Number.isFinite(mgmtNum) ? `${mgmtNum}%` : '—';
      const statusCls   = l.status === 'active' || !l.status ? 'pill-green' : l.status === 'inactive' ? 'pill-gray' : 'pill-amber';
      const thumb       = l.thumbnail_url;

      const channelLinks = this.renderChannelLinks(l);
      const isExcl = this.excluded.has(String(l.id));

      return `
        <tr style="${isExcl ? 'background:var(--bg-subtle); opacity:0.6;' : ''}">
          <td>
            <div style="display:flex; align-items:center; gap:var(--space-3); min-width:0;">
              ${thumb
                ? `<img src="${Formatters.escapeHtml(thumb)}" style="width:44px;height:34px;object-fit:cover;border-radius:var(--radius-sm);flex-shrink:0;${isExcl ? 'filter:grayscale(1);' : ''}" onerror="this.style.display='none'" />`
                : `<div style="width:44px;height:34px;background:var(--border-soft);border-radius:var(--radius-sm);flex-shrink:0;"></div>`}
              <div style="min-width:0;">
                <div class="u-truncate" style="font-weight:500; max-width:260px;" title="${Formatters.escapeHtml(l.name || '')}">${Formatters.escapeHtml(name)}</div>
                <div style="font-size:var(--fs-label); color:var(--text-muted); margin-top:2px;">
                  <span class="pill ${statusCls}">${Formatters.escapeHtml(l.status || 'active')}</span>
                  ${isExcl ? '<span class="pill pill-red" style="margin-left:4px;">Excluded</span>' : ''}
                  ${l.min_nights ? `<span style="margin-left:4px;">${l.min_nights}n min</span>` : ''}
                </div>
              </div>
            </div>
          </td>
          <td style="color:var(--text-muted);">${Formatters.escapeHtml(l.city || l.market_locality || '—')}</td>
          <td class="num" style="text-align:center;">${l.bedrooms ?? '—'}</td>
          <td class="num" style="text-align:center;">${l.max_guests ?? '—'}</td>
          <td style="text-align:center;">
            ${rating != null
              ? `<span style="font-weight:600; color:var(--warning);">★</span> <span style="font-weight:600;">${rating.toFixed(1)}</span>`
              : '<span style="color:var(--text-muted);">—</span>'}
          </td>
          <td class="num" style="text-align:center; font-weight:500;">${mgmt}</td>
          <td>${channelLinks}</td>
          <td style="text-align:center; white-space:nowrap;">
            <span style="display:inline-flex; gap:8px; justify-content:center;">
              <button class="btn btn-secondary btn-sm edit-listing-btn" data-id="${l.id}">Edit</button>
              <button class="btn btn-sm excl-toggle-btn ${isExcl ? 'btn-success' : 'btn-outline'}" data-id="${l.id}" data-excluded="${isExcl ? '1' : '0'}"
                title="${isExcl ? 'Re-include in the dashboard' : 'Exclude from the entire dashboard'}"
                ${isExcl ? '' : 'style="color:var(--danger);"'}>
                ${isExcl ? 'Include' : 'Exclude'}
              </button>
            </span>
          </td>
        </tr>`;
    }).join('');

    this.bindTableControls();
  }

  // Presentational summary tiles over the already-loaded roster.
  private renderKpis(): void {
    const el = document.getElementById('listings-kpis');
    if (!el) return;

    const total    = this.listings.length;
    const excluded = this.listings.filter(l => this.excluded.has(String(l.id))).length;
    const active   = this.listings.filter(l => (l.status || 'active') === 'active' && !this.excluded.has(String(l.id))).length;

    el.innerHTML = `
      <div class="kpi-card">
        <div class="kpi-card-top"><span class="kpi-card-label">Total Listings</span><span class="kpi-card-ic">${icon('home', 17)}</span></div>
        <div class="kpi-card-value">${total}</div>
        <div class="kpi-card-cap">properties in the portfolio roster</div>
      </div>
      <div class="kpi-card tone-good">
        <div class="kpi-card-top"><span class="kpi-card-label">Active</span><span class="kpi-card-ic">${icon('building', 17)}</span></div>
        <div class="kpi-card-value">${active}</div>
        <div class="kpi-card-cap">counting toward the dashboard</div>
      </div>
      <div class="kpi-card${excluded ? ' tone-bad' : ''}">
        <div class="kpi-card-top"><span class="kpi-card-label">Excluded</span><span class="kpi-card-ic">${icon('flag', 17)}</span></div>
        <div class="kpi-card-value">${excluded}</div>
        <div class="kpi-card-cap">hidden from every other page</div>
      </div>`;
  }

  // Wire the shared filter bar + pager controls after each render, then refocus
  // the search box (so typing isn't interrupted by the re-render).
  private bindTableControls(): void {
    const search = document.getElementById('listings-search') as HTMLInputElement | null;
    search?.addEventListener('input', () => {
      this.searchQuery = search.value;
      this.currentPage = 1;
      this.renderTable();
      const next = document.getElementById('listings-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });

    const city = document.getElementById('listings-city-filter') as HTMLSelectElement | null;
    city?.addEventListener('change', () => { this.filterCity = city.value; this.currentPage = 1; this.renderTable(); });

    const beds = document.getElementById('listings-bedrooms-filter') as HTMLSelectElement | null;
    beds?.addEventListener('change', () => { this.filterBedrooms = beds.value; this.currentPage = 1; this.renderTable(); });

    const status = document.getElementById('listings-status-filter') as HTMLSelectElement | null;
    status?.addEventListener('change', () => { this.filterStatus = status.value; this.currentPage = 1; this.renderTable(); });

    document.getElementById('listings-prev')?.addEventListener('click', () => { this.currentPage--; this.renderTable(); });
    document.getElementById('listings-next')?.addEventListener('click', () => { this.currentPage++; this.renderTable(); });
  }

  private renderChannelLinks(l: any): string {
    const channels: string[] = [];

    if (l.wch_url) {
      channels.push(`<a href="${Formatters.escapeHtml(l.wch_url)}" target="_blank" rel="noopener"
        title="View on West Coast Homestays"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:var(--radius-sm);font-size:11px;font-weight:600;text-decoration:none;background:var(--accent-soft);color:var(--accent);border:1px solid var(--accent-border);"
        >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        WCH
      </a>`);
    }

    if (l.airbnb_url) {
      channels.push(`<a href="${Formatters.escapeHtml(l.airbnb_url)}" target="_blank" rel="noopener"
        title="View on Airbnb"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;text-decoration:none;background:#ff5a5f1a;color:#ff5a5f;border:1px solid #ff5a5f40;"
        >
        <svg width="11" height="11" viewBox="0 0 32 32" fill="currentColor"><path d="M16 1C7.163 1 0 8.163 0 17c0 5.356 2.63 10.09 6.668 13.02L16 31l9.332-0.98C29.37 27.09 32 22.356 32 17 32 8.163 24.837 1 16 1zm0 27.5c-2.348 0-4.246-1.9-4.246-4.247S13.652 19.007 16 19.007s4.246 1.898 4.246 4.246S18.348 28.5 16 28.5zm9.004-14.627c-0.47 1.197-1.173 2.257-2.068 3.12-0.604 0.578-1.284 1.077-2.015 1.472l-1.82-4.642c-0.28-0.715-0.973-1.18-1.74-1.15h-2.72c-0.768-0.03-1.46 0.435-1.74 1.15l-1.82 4.642c-0.73-0.395-1.41-0.894-2.015-1.472-0.895-0.863-1.598-1.923-2.068-3.12-0.235-0.598-0.35-1.24-0.338-1.887 0.052-2.56 2.135-4.6 4.696-4.6 1.032 0 1.988 0.33 2.766 0.89l1.04 0.752 1.04-0.752c0.778-0.56 1.734-0.89 2.766-0.89 2.56 0 4.644 2.04 4.696 4.6 0.012 0.647-0.103 1.289-0.338 1.887z"/></svg>
        Airbnb
      </a>`);
    }

    if (l.vrbo_url) {
      channels.push(`<a href="${Formatters.escapeHtml(l.vrbo_url)}" target="_blank" rel="noopener"
        title="View on VRBO"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;text-decoration:none;background:#1e5c9b1a;color:#1e5c9b;border:1px solid #1e5c9b40;"
        >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        VRBO
      </a>`);
    }

    if (l.expedia_url) {
      channels.push(`<a href="${Formatters.escapeHtml(l.expedia_url)}" target="_blank" rel="noopener"
        title="View on Expedia"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;text-decoration:none;background:#f9b1281a;color:#c68b00;border:1px solid #f9b12840;"
        >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>
        Expedia
      </a>`);
    }

    if (l.google_vr_url) {
      channels.push(`<a href="${Formatters.escapeHtml(l.google_vr_url)}" target="_blank" rel="noopener"
        title="View on Google"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:11px;font-weight:600;text-decoration:none;background:#4285f41a;color:#4285f4;border:1px solid #4285f440;"
        >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/></svg>
        Google
      </a>`);
    }

    return channels.length
      ? `<div style="display:flex;flex-wrap:wrap;gap:4px;">${channels.join('')}</div>`
      : '<span style="color:var(--text-muted); font-size:12px;">—</span>';
  }

  // ── Filtering ─────────────────────────────────────────────────

  // Pure: derive filteredListings from the already-loaded listings + current
  // search/filter state. No DOM, no page reset (callers handle that).
  private computeFiltered(): void {
    const s = this.searchQuery.toLowerCase();
    this.filteredListings = this.listings.filter(l => {
      if (s) {
        const haystack = (l.name + ' ' + (l.short_name || '') + ' ' + (l.city || '')).toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      const city = l.city || l.market_locality || '';
      if (this.filterCity && city !== this.filterCity) return false;
      if (this.filterStatus && (l.status || '') !== this.filterStatus) return false;
      if (this.filterBedrooms === '5' && (l.bedrooms || 0) < 5) return false;
      if (this.filterBedrooms && this.filterBedrooms !== '5' && l.bedrooms != parseInt(this.filterBedrooms)) return false;
      return true;
    });
  }

  // ── Include / exclude ─────────────────────────────────────────

  // R11 #5: additive Hostaway import — dry-run first, show exactly what's new,
  // then insert on confirm. Existing listings are never modified.
  private async importNewListings(): Promise<void> {
    const btn = document.getElementById('import-new-listings') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Checking Hostaway…'; }
    try {
      const dryR = await fetch('/api/listings/import-new', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dry: true }) });
      const dry = await dryR.json();
      if (!dryR.ok) throw new Error(dry.error || 'import check failed');
      if (!dry.new_found.length) {
        alert(`No new listings found.\n\nHostaway has ${dry.hostaway_total} listings; all of them are already in the system (${dry.already_in_system} known).`);
        return;
      }
      const names = dry.new_found.map((l: any) => `  • ${l.name} (${l.bedrooms ?? '?'}bd / ${l.max_guests ?? '?'} guests)`).join('\n');
      if (!confirm(`Found ${dry.new_found.length} new listing${dry.new_found.length === 1 ? '' : 's'} on Hostaway:\n\n${names}\n\nImport them? Existing listings are NOT touched — names, fees, formulas and exclusions all stay as they are. New listings arrive as "active".`)) return;
      const realR = await fetch('/api/listings/import-new', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dry: false }) });
      const real = await realR.json();
      if (!realR.ok) throw new Error(real.error || 'import failed');
      alert(`Imported ${real.inserted} new listing${real.inserted === 1 ? '' : 's'}. They're active now — set short names / fees / exclusions as needed.`);
      await this.loadData();
    } catch (e: any) {
      alert(e.message || 'Import failed');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = '⇩ Import new from Hostaway'; }
    }
  }

  private async toggleExclude(id: string, exclude: boolean): Promise<void> {
    try {
      const r = await fetch('/api/listings/exclusions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, excluded: exclude }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      if (exclude) this.excluded.add(String(id)); else this.excluded.delete(String(id));
      this.renderTable(); // re-renders rows + refreshes the count label
    } catch (err) {
      console.error('Failed to toggle exclusion:', err);
      alert('Could not update that property. Please try again.');
    }
  }

  // ── Edit Modal ────────────────────────────────────────────────

  private openEditModal(id: string): void {
    const listing = this.listings.find(l => l.id === id);
    if (!listing) return;
    const val = (elId: string, v: string) => {
      const el = document.getElementById(elId) as HTMLInputElement | null;
      if (el) el.value = v;
    };
    val('edit-listing-id',  id);
    (document.getElementById('edit-listing-name') as HTMLElement).textContent = listing.name || '';
    val('edit-short-name',  listing.short_name  || '');
    val('edit-mgmt-fee',    String(listing.mgmt_fee_percent ?? this.defaultMgmtFee));
    val('edit-wch-url',     listing.wch_url     || '');
    val('edit-airbnb-url',  listing.airbnb_url  || '');
    val('edit-vrbo-url',    listing.vrbo_url    || '');
    document.getElementById('edit-listing-modal')?.classList.add('active');
  }

  private async saveListing(): Promise<void> {
    const get = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value.trim() || null;
    const id        = get('edit-listing-id')!;
    const shortName = get('edit-short-name');
    const mgmtFee   = parseFloat((document.getElementById('edit-mgmt-fee') as HTMLInputElement)?.value);
    const wchUrl    = get('edit-wch-url');
    const airbnbUrl = get('edit-airbnb-url');
    const vrboUrl   = get('edit-vrbo-url');

    // Derive pms_listing_id from WCH URL (the listing ID segment)
    const pmsId = wchUrl ? (wchUrl.split('/').pop() || null) : null;

    const btn = document.getElementById('save-listing-btn') as HTMLButtonElement;
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

    const { error } = await supabase
      .from('listings')
      .update({
        short_name:      shortName,
        mgmt_fee_percent: isNaN(mgmtFee) ? undefined : mgmtFee,
        wch_url:         wchUrl,
        airbnb_url:      airbnbUrl,
        vrbo_url:        vrboUrl,
        pms_listing_id:  pmsId,
      })
      .eq('id', id);

    if (btn) { btn.disabled = false; btn.textContent = 'Save Changes'; }
    if (error) { alert('Failed to save: ' + error.message); return; }
    document.getElementById('edit-listing-modal')?.classList.remove('active');
    await this.loadData();
  }

  private updateBadge(): void {
    const b = document.getElementById('listings-badge');
    if (b) b.textContent = String(this.listings.length);
  }

  // ── Export ────────────────────────────────────────────────────

  private exportCSV(): void {
    const headers = ['Name', 'Short Name', 'City', 'BR', 'Guests', 'Rating', 'Mgmt%', 'Status', 'Airbnb URL', 'VRBO URL', 'Expedia URL'];
    const rows = this.filteredListings.map(l => [
      l.name, l.short_name || '', l.city || l.market_locality || '',
      l.bedrooms || '', l.max_guests || '',
      l.review_score || '', l.mgmt_fee_percent ?? this.defaultMgmtFee,
      l.status || 'active',
      l.airbnb_url || '', l.vrbo_url || '', l.expedia_url || ''
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'listings.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ── Events ────────────────────────────────────────────────────

  private bindEvents(): void {
    // Search / filters / prev / next are wired per-render in bindTableControls()
    // (they live inside the re-rendered filter bar + pager).
    document.getElementById('refresh-listings')?.addEventListener('click', () => this.loadData());
    document.getElementById('export-listings')?.addEventListener('click', () => this.exportCSV());
    document.getElementById('import-new-listings')?.addEventListener('click', () => this.importNewListings());
    document.getElementById('listings-table-body')?.addEventListener('click', e => {
      const tog = (e.target as HTMLElement).closest<HTMLButtonElement>('.excl-toggle-btn');
      if (tog) { this.toggleExclude(tog.dataset.id!, tog.dataset.excluded !== '1'); return; }
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.edit-listing-btn');
      if (btn) this.openEditModal(btn.dataset.id!);
    });
    document.getElementById('save-listing-btn')?.addEventListener('click', () => this.saveListing());
    document.getElementById('cancel-edit-btn')?.addEventListener('click', () => {
      document.getElementById('edit-listing-modal')?.classList.remove('active');
    });
    document.getElementById('edit-listing-modal')?.addEventListener('click', e => {
      if (e.target === document.getElementById('edit-listing-modal')) {
        document.getElementById('edit-listing-modal')?.classList.remove('active');
      }
    });
  }
}
